import { useEffect, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Button, Card, SecondaryButton } from "@contractor-quoting/shared-ui";
import { SharedRateEditor } from "./SharedRateEditor.js";
import {
  minorFromMoney,
  moneyFromMinor,
  type PricingSettings,
  type SharedPricingResponse,
} from "./quoting-setup-model.js";

export function SharedPricingWorkspace({ onOpenImport }: { onOpenImport: () => void }) {
  const [data, setData] = useState<SharedPricingResponse>({ current: null, versions: [] });
  const [settings, setSettings] = useState<PricingSettings | null>(null);
  const [dirtySettings, setDirtySettings] = useState(false);
  const [saveState, setSaveState] = useState("");
  const [message, setMessage] = useState("");
  const load = async () => {
    const result = await apiFetch<SharedPricingResponse>("/v1/contractor/shared-pricing");
    setData(result);
    setSettings(result.current?.settings ?? null);
    setDirtySettings(false);
  };
  useEffect(() => {
    void load().catch((reason: unknown) => setMessage(errorMessage(reason)));
  }, []);
  useEffect(() => {
    const current = data.current;
    if (!dirtySettings || !current || current.status !== "draft" || !settings) return;
    const timeout = window.setTimeout(() => {
      setSaveState("Saving…");
      void apiFetch<{ revision: number }>(
        `/v1/contractor/shared-pricing/drafts/${current.versionId}/settings`,
        {
          method: "PATCH",
          body: JSON.stringify({ expectedRevision: current.revision, ...settings }),
        },
      )
        .then((result) => {
          setData((value) =>
            value.current
              ? { ...value, current: { ...value.current, revision: result.revision, settings } }
              : value,
          );
          setDirtySettings(false);
          setSaveState("Saved");
        })
        .catch((reason: unknown) => {
          setSaveState("Save failed");
          setMessage(errorMessage(reason));
        });
    }, 650);
    return () => window.clearTimeout(timeout);
  }, [data, dirtySettings, settings]);

  if (!data.current)
    return (
      <Card className="text-center">
        <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
          First-time setup
        </p>
        <h2 className="mt-2 text-2xl font-black">Add your shared pricing</h2>
        <p className="mx-auto mt-3 max-w-2xl text-stone-600">
          Mezoniq supplies the structure and stable rate codes, but never invents your prices.
          Upload the completed workbook to get started.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Button onClick={onOpenImport}>Upload shared pricing</Button>
          <a
            className="inline-flex min-h-12 items-center rounded-xl border border-emerald-800 px-5 font-bold text-emerald-900"
            href="/api/v1/contractor/configuration/pricing_book/template"
          >
            Download template
          </a>
        </div>
        {message ? <p className="mt-4 text-red-700">{message}</p> : null}
      </Card>
    );

  const current = data.current;
  const editable = current.status === "draft";
  const customize = async () => {
    const result = await apiFetch<SharedPricingResponse>("/v1/contractor/shared-pricing/drafts", {
      method: "POST",
    });
    setData(result);
    setSettings(result.current?.settings ?? null);
    setMessage("Pricing draft ready. Changes now autosave.");
  };
  const publish = async () => {
    if (dirtySettings) return;
    await apiFetch(`/v1/contractor/shared-pricing/drafts/${current.versionId}/publish`, {
      method: "POST",
    });
    setMessage("Shared pricing published. Activate it to use it with enabled runbooks.");
    await load();
  };
  const activate = async () => {
    await apiFetch(`/v1/contractor/shared-pricing/versions/${current.versionId}/activate`, {
      method: "POST",
    });
    setMessage("Shared pricing activated for compatible enabled runbooks.");
    await load();
  };
  return (
    <div>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
              Current shared catalog
            </p>
            <h2 className="mt-1 text-2xl font-black">{current.name}</h2>
            <div className="mt-2 flex gap-2">
              <Badge>{current.status}</Badge>
              {current.active ? <Badge>Active</Badge> : null}
              <span className="self-center text-sm text-stone-500">Version {current.version}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-stone-500">{saveState}</span>
            {editable ? (
              <Button disabled={dirtySettings} onClick={() => void publish()}>
                Publish draft
              </Button>
            ) : (
              <Button onClick={() => void customize()}>Edit pricing</Button>
            )}
            {current.status === "published" && !current.active ? (
              <SecondaryButton onClick={() => void activate()}>Activate</SecondaryButton>
            ) : null}
          </div>
        </div>
        {message ? (
          <p className="mt-4 rounded-xl bg-emerald-50 p-3 font-semibold text-emerald-900">
            {message}
          </p>
        ) : null}
        {settings ? (
          <SettingsEditor
            editable={editable}
            settings={settings}
            onChange={(next) => {
              setSettings(next);
              setDirtySettings(true);
              setSaveState("");
            }}
          />
        ) : null}
      </Card>
      <SharedRateEditor
        current={current}
        editable={editable}
        onChanged={() => void load()}
        onError={setMessage}
      />
      {data.versions.length > 1 ? (
        <Card className="mt-6">
          <h3 className="font-black">Version history</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.versions.map((version) => (
              <Badge
                className={version.active ? "" : "bg-stone-100 text-stone-700"}
                key={version.id}
              >
                v{version.version} · {version.status}
                {version.active ? " · active" : ""}
              </Badge>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function SettingsEditor({
  settings,
  editable,
  onChange,
}: {
  settings: PricingSettings;
  editable: boolean;
  onChange: (settings: PricingSettings) => void;
}) {
  const basisPoints = (key: keyof PricingSettings, value: string) =>
    onChange({ ...settings, [key]: Math.round(Number(value || 0) * 100) });
  return (
    <div className="mt-6">
      <h3 className="font-black">Catalog settings</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Setting label="Currency">
          <input
            disabled={!editable}
            value={settings.currency}
            onChange={(event) =>
              onChange({ ...settings, currency: event.target.value.toUpperCase() })
            }
          />
        </Setting>
        <Setting label="Minimum charge">
          <input
            disabled={!editable}
            type="number"
            step="0.01"
            value={moneyFromMinor(settings.minimumChargeMinor)}
            onChange={(event) =>
              onChange({ ...settings, minimumChargeMinor: minorFromMoney(event.target.value) })
            }
          />
        </Setting>
        <Setting label="Tax %">
          <input
            disabled={!editable}
            type="number"
            step="0.01"
            value={settings.taxBps / 100}
            onChange={(event) => basisPoints("taxBps", event.target.value)}
          />
        </Setting>
        <Setting label="Tax label">
          <input
            disabled={!editable}
            value={settings.taxLabel}
            onChange={(event) => onChange({ ...settings, taxLabel: event.target.value })}
          />
        </Setting>
        <Setting label="Measurement allowance %">
          <input
            disabled={!editable}
            type="number"
            step="0.01"
            value={settings.measurementAllowanceBps / 100}
            onChange={(event) => basisPoints("measurementAllowanceBps", event.target.value)}
          />
        </Setting>
        <Setting label="Material waste %">
          <input
            disabled={!editable}
            type="number"
            step="0.01"
            value={settings.materialWasteBps / 100}
            onChange={(event) => basisPoints("materialWasteBps", event.target.value)}
          />
        </Setting>
        <Setting label="Markup %">
          <input
            disabled={!editable}
            type="number"
            step="0.01"
            value={settings.markupBps / 100}
            onChange={(event) => basisPoints("markupBps", event.target.value)}
          />
        </Setting>
        <Setting label="Pricing contingency %">
          <input
            disabled={!editable}
            type="number"
            step="0.01"
            value={settings.contingencyBps / 100}
            onChange={(event) => basisPoints("contingencyBps", event.target.value)}
          />
        </Setting>
      </div>
    </div>
  );
}

function Setting({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <label className="block text-sm font-bold text-stone-700">
      <span className="mb-1 block">{label}</span>
      <span className="[&>input]:w-full [&>input]:rounded-lg [&>input]:border [&>input]:p-2">
        {children}
      </span>
    </label>
  );
}

function errorMessage(reason: unknown) {
  return reason instanceof Error ? reason.message : "Shared pricing could not be updated.";
}
